import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { postPretransfer, postTransfer, getTransfer } from '../../lib/api';
import { popupCenter } from '../../lib/utils';

const initialState = {
  payPalLink: null,
  transferId: null,
  cardNumber: null,
  expirationDate: null,
  cardCode: null,
  firstName: null,
  lastName: null,
  address: null,
  zip: null,
};

export const callPreTransfer = createAsyncThunk(
  'transfer/callPreTransfer',
  async (paymentMethodType, { getState }) => {
    const state = getState();

    const receiverCountryCode = state.receiver.countryCode;
    const receiverPhoneNumber = state.receiver.fullPhoneNumber;
    const receiverCarrier = state.networks.selected.name;
    const transferType = state.transferTypes.selected;
    const amount = state.product.amount;
    const receiptEmail = state.sender.email;
    const sku = state.product.sku;

    const data = {
      receiverCountryCode,
      receiverPhoneNumber,
      receiverCarrier,
      transferType,
      paymentMethodType,
      amount,
      receiptEmail,
      sku,
    };

    try {
      const response = await postPretransfer(data);

      return response;
    } catch (error) {
      return Promise.reject();
    }
  }
);

export const payWithPayPal = createAsyncThunk('transfer/payWithPayPal', async (_, { getState }) => {
  const { payPalLink, transferId } = getState().transfer;

  const child = popupCenter({ url: payPalLink, title: 'window' });
  window.history.pushState({}, document.title, '/topup');

  const newWindowPromise = (window) =>
    new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        if (window && window.closed) {
          clearInterval(interval);
          resolve();
        }
      }, 500);
    });

  if (!child) {
    return Promise.reject('popup-blocked');
  }

  await newWindowPromise(child);

  const response = await getTransfer(transferId);

  if (response.transfer.status > 1 && response.transfer.status !== 5) {
    return Promise.resolve();
  } else {
    return Promise.reject();
  }
});

export const payWithCreditCard = createAsyncThunk(
  'transfer/payWithCreditCard',
  async ({ email, accessToken }, { getState }) => {
    const state = getState();

    const receiverCountryCode = state.receiver.countryCode;
    const receiverPhoneNumber = state.receiver.fullPhoneNumber;
    const receiverCarrier = state.networks.selected.name;
    const transferType = state.transferTypes.selected;
    const paymentMethodType = state.paymentMethods.selected;
    const amount = state.product.amount;
    const sku = state.product.sku;

    const data = {
      receiverCountryCode,
      receiverPhoneNumber,
      receiverCarrier,
      transferType,
      paymentMethodType,
      amount,
      receiptEmail: email,
      sku,
    };

    const prepaymentResponse = await postPretransfer(data, accessToken);

    const transferData = state.transfer;

    const paymentData = {
      transferId: prepaymentResponse.transferId,
      cardNumber: transferData.cardNumber,
      expirationDate: transferData.expirationDate,
      cardCode: transferData.cardCode,
      firstName: transferData.firstName,
      lastName: transferData.lastName,
      address: transferData.address,
      zip: transferData.zip,
    };

    const paymentResponse = await postTransfer(paymentData, accessToken);

    if (paymentResponse.completed) {
      return Promise.resolve({ transferId: prepaymentResponse.transferId });
    } else {
      return Promise.reject();
    }
  }
);

const transferSlice = createSlice({
  name: 'transfer',
  initialState,
  reducers: {
    setCreditCardData(state, action) {
      state.cardNumber = action.payload.cardNumber.split(' ').join('');
      state.expirationDate = action.payload.expirationDate.split('/').join('');
      state.cardCode = action.payload.cardCode;
      state.firstName = action.payload.firstName;
      state.lastName = action.payload.lastName;
      state.address = action.payload.address;
      state.zip = action.payload.zip;
    },
  },
  extraReducers: {
    [callPreTransfer.fulfilled]: (state, action) => {
      state.payPalLink = action.payload.payPalLink;
      state.transferId = action.payload.transferId;
    },

    [payWithCreditCard.fulfilled]: (state, action) => ({
      ...initialState,
      transferId: action.payload.transferId,
    }),
    RESET: () => initialState,
  },
});

export const { setCreditCardData } = transferSlice.actions;

export default transferSlice.reducer;
